/**
 * PartCodePickerDialog Component
 * Dialog for selecting a part code from categories
 */

import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { usePartCodeCategories, usePartCodes } from '@/hooks/usePSI';
import type { ICategoryInfo, IPartCode } from '@/types/psi';

interface PartCodePickerDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (partCode: IPartCode, categoryId: string) => void;
}

export const PartCodePickerDialog: React.FC<PartCodePickerDialogProps> = ({
  isOpen,
  onClose,
  onSelect,
}) => {
  const { t } = useTranslation('psi');
  const [selectedCategory, setSelectedCategory] = useState<ICategoryInfo | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch categories
  const { data: categories = [], isLoading: categoriesLoading } = usePartCodeCategories();

  // Fetch part codes for selected category
  const { data: partCodes = [], isLoading: partCodesLoading } = usePartCodes(
    selectedCategory?.id || ''
  );

  // Filter part codes by search term
  const filteredPartCodes = useMemo(() => {
    if (!searchTerm.trim()) return partCodes;
    const term = searchTerm.toLowerCase();
    return partCodes.filter(
      (pc) =>
        pc.part_number.toLowerCase().includes(term) ||
        pc.vendor_product.toLowerCase().includes(term) ||
        pc.vendor_spec.toLowerCase().includes(term) ||
        pc.brand.toLowerCase().includes(term)
    );
  }, [partCodes, searchTerm]);

  const handleCategorySelect = (category: ICategoryInfo) => {
    setSelectedCategory(category);
    setSearchTerm('');
  };

  const handlePartCodeSelect = (partCode: IPartCode) => {
    if (selectedCategory) {
      onSelect(partCode, selectedCategory.id);
      onClose();
    }
  };

  const handleBack = () => {
    setSelectedCategory(null);
    setSearchTerm('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {selectedCategory && (
              <button
                onClick={handleBack}
                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {selectedCategory
                ? `${t('quotations.dialog.selectPartCode')} - ${selectedCategory.name}`
                : t('quotations.dialog.selectCategory')}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {!selectedCategory ? (
            // Category Selection
            <div className="space-y-4">
              {categoriesLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {categories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => handleCategorySelect(category)}
                      className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-left"
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-indigo-100 dark:bg-indigo-900 rounded-lg">
                          <svg className="w-5 h-5 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                          </svg>
                        </div>
                        <div>
                          <div className="font-medium text-gray-900 dark:text-gray-100">
                            {category.name}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {category.sheet_name}
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            // Part Code Selection
            <div className="space-y-4">
              {/* Search Input */}
              <div className="relative">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={t('quotations.dialog.searchPartCode')}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                           bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
                           focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
                <svg
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>

              {/* Part Codes List */}
              {partCodesLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                </div>
              ) : filteredPartCodes.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  {searchTerm
                    ? t('quotations.dialog.noPartCodesFound')
                    : t('quotations.dialog.noPartCodesInCategory')}
                </div>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {filteredPartCodes.map((partCode) => (
                    <button
                      key={partCode.row_index}
                      onClick={() => handlePartCodeSelect(partCode)}
                      className="w-full p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-900/20 hover:border-indigo-300 dark:hover:border-indigo-700 transition-colors text-left"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-medium text-indigo-600 dark:text-indigo-400">
                              {partCode.part_number}
                            </span>
                            {partCode.brand && (
                              <span className="px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 rounded">
                                {partCode.brand}
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-600 dark:text-gray-300 mt-1 truncate">
                            {partCode.vendor_product}
                          </div>
                          {partCode.vendor_spec && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                              {partCode.vendor_spec}
                            </div>
                          )}
                        </div>
                        <svg
                          className="w-5 h-5 text-gray-400 flex-shrink-0 ml-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
          >
            {t('common.cancel')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PartCodePickerDialog;
